import ConfirmModal from "@app/components/confirm-modal";
import RejectReasonModal from "@app/components/reject-reason-modal";
import Table, { ColumnConfig } from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
} from "@app/constants";
import AuthorBankService from "@app/services/http/author-bank.service";
import { openPortalDialog } from "@app/services/modal.service";
import { AuthorBank, Meta } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";
import ApproveIcon from "@app/icons/approve";
import RejectIcon from "@app/icons/reject";

const formatDate = (timestamp: number) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const columnConfig: ColumnConfig[] = [
  {
    label: "Tác giả",
    dataKey: "fullName",
    width: "12%",
    hasOrder: true,
  },
  {
    label: "Ngân hàng",
    dataKey: "bankName",
    width: "10%",
    hasOrder: true,
  },
  {
    label: "Số tài khoản",
    dataKey: "bankNumber",
    width: "10%",
    hasOrder: true,
  },
  {
    label: "Chủ tài khoản",
    dataKey: "bankOwner",
    width: "12%",
    hasOrder: true,
  },
  {
    label: "CMND/CCCD",
    dataKey: "identityNumber",
    width: "10%",
  },
  {
    label: "Ngày cấp",
    dataKey: "issuedDate",
    width: "8%",
    component: ({ data }: { data: AuthorBank }) => (
      <span>{formatDate(data.issuedDate)}</span>
    ),
  },
  {
    label: "Nơi cấp",
    dataKey: "issuedPlace",
    width: "10%",
  },
  {
    label: "Địa chỉ người thụ hưởng",
    dataKey: "beneficiaryAddress",
    width: "12%",
  },
  {
    label: "Lý do từ chối",
    dataKey: "reason",
    width: "12%",
    component: ({ data }: { data: AuthorBank }) => (
      <span className="text-red-600">
        {data.reason || "-"}
      </span>
    ),
  },
  {
    label: "Trạng thái",
    dataKey: "isActive",
    width: "12%",
    component: ({ data }: { data: AuthorBank }) => (
      <span className={data.deleteFlag ? "text-red-600" : (data.isActive ? "text-green-600" : "text-orange-600")}>
        {data.deleteFlag ? "Đã từ chối" : (data.isActive ? "Đã duyệt" : "Chờ duyệt")}
      </span>
    ),
  },
];

function AuthorBankApproval() {
  const [authorBanks, setAuthorBanks] = useState<AuthorBank[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();
  const [update, forceUpdate] = useForceUpdate();
  const { abortController } = useCancellationRequest();

  useEffect(() => {
    AuthorBankService.getList(requestParams, abortController.signal).then((res) => {
      setAuthorBanks(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: { orderBy: string; order: string }) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleApproveClick = (authorBank: AuthorBank) => {
    const confirmModalObs = openPortalDialog(ConfirmModal, {
      message: `Bạn có chắc chắn muốn duyệt liên kết ngân hàng của ${authorBank.fullName}?`,
    });

    confirmModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        AuthorBankService.approve(authorBank.id).then(() => {
          addToast({ text: "Duyệt liên kết ngân hàng thành công" });
          forceUpdate();
        });
      }
    });
  };

  const handleRejectClick = (authorBank: AuthorBank) => {
    const rejectModalObs = openPortalDialog(RejectReasonModal, {
      authorName: authorBank.fullName,
    });

    rejectModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept && data?.reason) {
        AuthorBankService.reject(authorBank.id, data.reason).then(() => {
          addToast({ text: "Từ chối liên kết ngân hàng thành công" });
          forceUpdate();
        });
      }
    });
  };

  const ActionsColumn = ({ data }: { data: AuthorBank }) => (
    <div className="flex justify-center gap-3">
      {!data.deleteFlag && (
        <>
          <div
            className="cursor-pointer hover:opacity-80"
            onClick={() => handleApproveClick(data)}
            title="Duyệt"
          >
            <ApproveIcon />
          </div>
          <div
            className="cursor-pointer hover:opacity-80"
            onClick={() => handleRejectClick(data)}
            title="Từ chối"
          >
            <RejectIcon />
          </div>
        </>
      )}
    </div>
  );

  const columnsWithActions: ColumnConfig[] = [
    ...columnConfig,
    {
      label: "Hành động",
      dataKey: "actions",
      width: "5%",
      component: ActionsColumn,
    },
  ];

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A] text-2xl font-semibold">
          Duyệt Liên Kết Ngân Hàng
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách chờ duyệt"
          onInputChange={onSearchChange}
        />
        <div className="mt-6 overflow-x-auto">
          <Table
            columnConfig={columnsWithActions}
            dataTable={authorBanks}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default AuthorBankApproval;
